import { useEffect } from "react";
import { FieldErrors } from "react-hook-form";

import type { AddDeviceFormType } from "@/apps/partner/types";

// Map of field names to their corresponding step IDs for scrolling
const FIELD_TO_STEP_MAP: Record<string, string> = {
  // Sim Selection (Step 1)
  simType: "partner-select-sim",

  // Sim Verification (Step 1 - when simType is TRIPLESIM)
  iccid: "partner-sim-verification",
  puk: "partner-sim-verification",

  simAlias: "partner-rate-selection",

  // Rate Selection (Step 2)
  productId: "partner-rate-selection",

  // Personal Details (Step 4)
  mail: "partner-personal-details",
  "customer.firstName": "first-name-input",
  "customer.lastName": "last-name-input",
  "customer.contactPhoneNumber": "phone-number-input",
  "customer.city": "city-input",
  "customer.countryCodeAlpha2": "countryCombobox",
  "customer.houseNumber": "house-number-input",
  "customer.street": "street-input",
  "customer.zip": "zip-code-input",
  "customer.salutationEnum": "partner-form-of-address",
  "customer.birthday": "birth-date-input",
  "customer.postalDeliveryNotes": "address-supplement-input",
  "customer.languageEnum": "partner-contact-language",
};

// Define the logical order of steps for prioritization
const STEP_PRIORITY_ORDER = [
  "partner-select-sim",
  "partner-sim-verification",
  "partner-rate-selection",
  "partner-personal-details",
  "partner-form-of-address",
  "partner-contact-language",
  "first-name-input",
  "last-name-input",
  "phone-number-input",
  "city-input",
  "countryCombobox",
  "house-number-input",
  "street-input",
  "zip-code-input",
  "birth-date-input",
  "address-supplement-input",
];

/**
 * Custom hook that automatically scrolls to the first step with validation errors
 * @param errors - Form validation errors from react-hook-form
 * @param shouldScroll - Optional flag to control when scrolling should occur
 * @param isSubmitting - Whether the form is currently being submitted
 * @param submitCount - Number of form submission attempts
 */
export const useScrollToFirstError = (
  errors: FieldErrors<AddDeviceFormType>,
  shouldScroll: boolean = true,
  isSubmitting?: boolean,
  submitCount?: number,
) => {
  useEffect(() => {
    if (!shouldScroll || !errors || Object.keys(errors).length === 0) {
      return;
    }

    // Find the first step with errors based on form flow order, not DOM position
    const findFirstErrorStep = (): string | null => {
      // Flatten nested error structure to get all field paths
      const getAllErrorFields = (errors: any): string[] => {
        const fields: string[] = [];

        // Handle top-level errors
        for (const key in errors) {
          if (errors[key] && typeof errors[key] === "object") {
            if (errors[key].message) {
              // This is an error object
              fields.push(key);
            } else {
              // This is a nested object (like customer or mnpData)
              for (const nestedKey in errors[key]) {
                if (errors[key][nestedKey]) {
                  fields.push(`${key}.${nestedKey}`);
                }
              }
            }
          }
        }

        return fields;
      };

      const errorFields = getAllErrorFields(errors);

      // Get unique step IDs from error fields
      const stepIds = [
        ...new Set(
          errorFields.map((field) => FIELD_TO_STEP_MAP[field]).filter(Boolean),
        ),
      ];

      // Find the first step with errors based on logical form order. Every
      // value in FIELD_TO_STEP_MAP is part of STEP_PRIORITY_ORDER, so a
      // non-empty stepIds always resolves here - there is no field-id fallback.
      for (const priorityStep of STEP_PRIORITY_ORDER) {
        if (stepIds.includes(priorityStep)) {
          return priorityStep;
        }
      }

      return null;
    };

    // Retry mechanism with multiple attempts to ensure DOM is stable
    const attemptScroll = (attempts = 0) => {
      const maxAttempts = 5;
      const firstErrorStep = findFirstErrorStep();

      if (firstErrorStep) {
        const targetElement = document.getElementById(firstErrorStep);

        if (targetElement) {
          // Scroll to the element with some offset to ensure it's at the very top
          const elementRect = targetElement.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const offset = 20; // Small offset to ensure element is clearly visible at top

          window.scrollTo({
            top: absoluteElementTop - offset,
            behavior: "smooth",
          });
        } else {
          if (attempts < maxAttempts) {
            // Retry with exponential backoff
            const delay = Math.min(100 * Math.pow(2, attempts), 1000);
            setTimeout(() => attemptScroll(attempts + 1), delay);
          }
        }
      } else if (attempts < maxAttempts) {
        // No step found, retry
        const delay = Math.min(100 * Math.pow(2, attempts), 1000);
        setTimeout(() => attemptScroll(attempts + 1), delay);
      }
    };

    // Start with initial attempt
    attemptScroll();
  }, [errors, shouldScroll, isSubmitting, submitCount]);
};
